import { formatDateTime, toDate } from '../../shared/value-utils';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { IValidator, Validator } from '../../shared/validation/validators';
import { Utils } from '../../shared/utils';
import { FieldState } from '../../shared/models/field-state';
import { CustomerService } from '../shared/services/customer.service';
import { ConfigurationService } from '../../shared/services/configuration.service';
import { CustomerModel } from '../shared/models/customer-model';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-customer-edit-verified',
  templateUrl: './customer-edit-verified.component.html',
  styleUrls: ['./customer-edit-verified.component.less'],
  standalone: false,
})
export class CustomerEditVerifiedComponent implements OnInit {
  @Output()
  public onUpdate: EventEmitter<CustomerModel> =
    new EventEmitter<CustomerModel>();

  @Input('customer')
  public customer: CustomerModel;

  private customerInput: CustomerModel;

  public isInEditMode: Boolean = false;

  public dateFormat: string;

  public specialDateFormat: string;

  public fieldStates: {
    verificationExpireDate: FieldState;
    isVerified: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private config: ConfigurationService,
    private customerService: CustomerService,
    private translationService: TranslateService
  ) {
    this.customerInput = new CustomerModel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customer'].currentValue != null) {
      this.customerInput = Utils.cloneObject(this.customer, CustomerModel);
      this.customerInput.verificationExpireDate =
        this.customer.verificationExpireDate != null
          ? new Date(this.customer.verificationExpireDate)
          : null;
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      verificationExpireDate: new FieldState(
        () => this.customerInput && this.customerInput.verificationExpireDate
      ),
      isVerified: new FieldState(
        () => this.customerInput && this.customerInput.industryId
      ),
    };
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  public onSaveClick() {
    let customer = this.customerInput;
    let varlidators = this.getValidators();
    this.invokeValidation(varlidators, () => {
      this.customerService.saveCustomerDetails(customer).subscribe(() => {
        this.initializeFieldsState();
        this.isInEditMode = false;
        this.onUpdate.emit(customer);
      });
    });
  }

  public onEditClick() {
    this.isInEditMode = true;
  }

  public onCancelClick() {
    this.isInEditMode = false;
    this.customerInput = Utils.cloneObject(this.customer, CustomerModel);
    this.initializeFieldsState();
    this.onUpdate.emit(this.customerInput);
  }

  getSpecialDateFormat(format: string) {
    return format.replace('MM', 'mm').replace('yyyy', 'yy');
  }

  private getValidators() {
    let field = this.field;
    let customerInput = this.customerInput;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );

    return [
      customerInput.isVerified == true
        ? Validator.forRequired(
            field.verificationExpireDate,
            () => customerInput.verificationExpireDate,
            emptyMessage
          )
        : Validator.forBypass(field.verificationExpireDate),
      customerInput.verificationExpireDate != null
        ? Validator.forPredicate(
            field.isVerified,
            () => customerInput.isVerified == true,
            emptyMessage
          )
        : Validator.forBypass(field.isVerified),
    ];
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.dateFormat = config.dateFormat; //TODO get from config
    this.specialDateFormat = this.getSpecialDateFormat(this.dateFormat);
  }
}
